import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  daysInMonth,
  formatIsoDate,
  isoToday,
  MONTHS,
  parseIsoDate,
  toIsoDate,
} from '../utils/date'

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

function monthOf(date: Date): { year: number; month: number } {
  return { year: date.getFullYear(), month: date.getMonth() }
}

interface DateFieldProps {
  id?: string
  /** yyyy-MM-dd, or '' for no date chosen. */
  value: string
  onChange: (value: string) => void
  /** Selectable range, both yyyy-MM-dd and both inclusive. */
  min: string
  max: string
  placeholder?: string
  /** Month the calendar opens on when nothing is selected yet. Defaults to the month of `max`. */
  openAt?: string
}

/**
 * Date picker over a yyyy-MM-dd string.
 *
 * Deliberately not `<input type="date">`: for a date of birth the native control opens on the
 * current month, so a learner born in 2008 is ~200 clicks from their birthday, and it accepts any
 * year at all. This one leads with a year list bounded by `min`/`max`, so the range is the
 * caller's to state and out-of-range days cannot be picked at all.
 */
export function DateField({
  id,
  value,
  onChange,
  min,
  max,
  placeholder = 'Select a date',
  openAt,
}: DateFieldProps) {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const minDate = useMemo(() => parseIsoDate(min), [min])
  const maxDate = useMemo(() => parseIsoDate(max), [max])
  const selected = useMemo(() => parseIsoDate(value), [value])

  const fallback = useMemo(
    () => parseIsoDate(openAt) ?? maxDate ?? new Date(),
    [openAt, maxDate],
  )
  const [view, setView] = useState(() => monthOf(selected ?? fallback))

  function toggle() {
    // Reopening on a stale month is disorienting, so each open starts from the selection. Done
    // here rather than in an effect: it is the click that should move the view, not a render.
    if (!open) setView(monthOf(selected ?? fallback))
    setOpen(!open)
  }

  useEffect(() => {
    if (!open) return
    function onPointerDown(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false)
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const years = useMemo(() => {
    const from = minDate?.getFullYear() ?? new Date().getFullYear() - 100
    const to = maxDate?.getFullYear() ?? new Date().getFullYear()
    // Newest first: a date of birth is far likelier to be recent than a century old.
    return Array.from({ length: to - from + 1 }, (_, i) => to - i)
  }, [minDate, maxDate])

  function outOfRange(date: Date): boolean {
    if (minDate && date < minDate) return true
    if (maxDate && date > maxDate) return true
    return false
  }

  /** True when every day of a month falls outside the range, so there is nothing to show. */
  function monthEmpty(year: number, month: number): boolean {
    return (
      outOfRange(new Date(year, month, 1)) &&
      outOfRange(new Date(year, month, daysInMonth(year, month)))
    )
  }

  function shiftMonth(delta: number) {
    setView((prev) => {
      const next = new Date(prev.year, prev.month + delta, 1)
      return { year: next.getFullYear(), month: next.getMonth() }
    })
  }

  const today = isoToday()
  const leadingBlanks = new Date(view.year, view.month, 1).getDay()
  const dayCount = daysInMonth(view.year, view.month)

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        id={id}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={toggle}
        className="input flex items-center justify-between gap-2 text-left"
      >
        <span className={value ? 'text-ink-900' : 'text-ink-300'}>
          {formatIsoDate(value) || placeholder}
        </span>
        <CalendarDays size={16} className="shrink-0 text-ink-400" aria-hidden="true" />
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Choose a date"
          className="absolute left-0 z-20 mt-1 w-[17.5rem] max-w-[calc(100vw-2rem)] rounded-xl border border-ink-200 bg-white p-3 shadow-lg"
        >
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              aria-label="Previous month"
              disabled={monthEmpty(view.year, view.month - 1)}
              onClick={() => shiftMonth(-1)}
              className="rounded-lg p-1.5 text-ink-500 transition hover:bg-ink-100 disabled:opacity-30 disabled:hover:bg-transparent"
            >
              <ChevronLeft size={16} />
            </button>
            <select
              aria-label="Month"
              value={view.month}
              onChange={(e) => setView((prev) => ({ ...prev, month: Number(e.target.value) }))}
              className="min-w-0 flex-1 rounded-lg border border-ink-200 bg-white px-1.5 py-1 text-sm"
            >
              {MONTHS.map((name, index) => (
                <option key={name} value={index} disabled={monthEmpty(view.year, index)}>
                  {name}
                </option>
              ))}
            </select>
            <select
              aria-label="Year"
              value={view.year}
              onChange={(e) => setView((prev) => ({ ...prev, year: Number(e.target.value) }))}
              className="rounded-lg border border-ink-200 bg-white px-1.5 py-1 text-sm"
            >
              {years.map((year) => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
            <button
              type="button"
              aria-label="Next month"
              disabled={monthEmpty(view.year, view.month + 1)}
              onClick={() => shiftMonth(1)}
              className="rounded-lg p-1.5 text-ink-500 transition hover:bg-ink-100 disabled:opacity-30 disabled:hover:bg-transparent"
            >
              <ChevronRight size={16} />
            </button>
          </div>

          <div className="mt-2 grid grid-cols-7 gap-0.5 text-center">
            {WEEKDAYS.map((day) => (
              <span key={day} className="py-1 text-[0.65rem] font-semibold uppercase text-ink-400">
                {day}
              </span>
            ))}
            {Array.from({ length: leadingBlanks }, (_, i) => <span key={`blank-${i}`} />)}
            {Array.from({ length: dayCount }, (_, i) => {
              const day = i + 1
              const date = new Date(view.year, view.month, day)
              const iso = toIsoDate(date)
              const disabled = outOfRange(date)
              const isSelected = iso === value
              return (
                <button
                  key={iso}
                  type="button"
                  disabled={disabled}
                  aria-current={isSelected ? 'date' : undefined}
                  onClick={() => {
                    onChange(iso)
                    setOpen(false)
                  }}
                  className={`rounded-lg py-1.5 text-sm transition ${
                    isSelected
                      ? 'bg-sun-400 font-semibold text-ink-900'
                      : disabled
                        ? 'cursor-not-allowed text-ink-200'
                        : `text-ink-700 hover:bg-ink-100 ${iso === today ? 'ring-1 ring-inset ring-sun-400' : ''}`
                  }`}
                >
                  {day}
                </button>
              )
            })}
          </div>

          {value && (
            <button
              type="button"
              onClick={() => {
                onChange('')
                setOpen(false)
              }}
              className="mt-2 w-full rounded-lg py-1.5 text-xs font-medium text-ink-500 transition hover:bg-ink-100"
            >
              Clear
            </button>
          )}
        </div>
      )}
    </div>
  )
}
